import _ from "lodash";
import React from "react";
import {
  ShareCurveOutlined,
  DownloadOutlined,
  PencilLineOutlined,
  DeleteOutlined,
  UserProfileOutlined,
  StudentOutlined,
  FamilyOutlined,
  GridOutlined,
  ListDashedOutlined,
} from "@toddle-edu/ds-icons";
import { getFileType } from "Utils";
import {
  getOrganizationResourcesFeedQuery,
  getOrganizationFolderResourcesQuery,
} from "./AdminEducatorCenterV2Query";
import { mapleBearLikeOrg } from "Courses/modules/utils";
import ACLStore from "lib/aclStore";
import {
  EDUCATOR_CENTER_REVAMP_PARENT_PERM,
  SCHOOL_POLICIES_SELECTED_PROGRAMS_FOR_CCH,
  SCHOOL_POLICIES_FOR_CCH,
} from "Constants/permissionConstants";
import { getSettingValue } from "modules/PermissionModule";

const generateDisabledTooltip = (t, derivedResourceType, action) => {
  const label =
    derivedResourceType === "FOLDER"
      ? _.lowerCase(t("common:folder"))
      : _.lowerCase(t("common:file"));

  return t("educatorCenter:this_cannot_be_with_label_and_action", {
    label,
    action: _.lowerCase(t(action)),
  });
};

const generateAccountDisabledTooltip = t => {
  return t("educatorCenter:account_level_action_disabled");
};

const getActionItems = ({
  t,
  derivedResourceType,
  onActionClick,
  resource,
  isAdminPage = false,
  disabled = false,
  isFromAccount = false,
}) => {
  return [
    {
      key: "SHARE",
      label: t("common:share"),
      icon: <ShareCurveOutlined />,
      resourceTypes: ["FILE", "FOLDER", "LINK"],
      clickAction: key => onActionClick(key, [resource]),
      visible: isAdminPage,
      disabled: disabled || isFromAccount,
      disabledTooltip: isFromAccount
        ? generateAccountDisabledTooltip(t)
        : generateDisabledTooltip(
            t,
            derivedResourceType,
            "educatorCenter:shared_or_modified"
          ),
      tooltipPlacement: "left",
    },
    {
      key: "DOWNLOAD",
      label: t("common:download"),
      icon: <DownloadOutlined />,
      resourceTypes: ["FILE"],
      clickAction: key => onActionClick(key, [resource]),
      visible: true,
      disabled: false,
    },
    {
      key: "RENAME",
      label: t("common:rename"),
      icon: <PencilLineOutlined />,
      resourceTypes: ["FILE", "FOLDER", "LINK"],
      clickAction: key => onActionClick(key, [resource]),
      visible: isAdminPage,
      disabled: disabled || isFromAccount,
      disabledTooltip: isFromAccount
        ? generateAccountDisabledTooltip(t)
        : generateDisabledTooltip(t, derivedResourceType, "common:renamed"),
      tooltipPlacement: "left",
    },
    {
      key: "DELETE",
      label: t("common:delete"),
      isDestructive: true,
      icon: <DeleteOutlined />,
      resourceTypes: ["FILE", "FOLDER", "LINK"],
      clickAction: key => onActionClick(key, [resource]),
      visible: isAdminPage,
      disabled: disabled || isFromAccount,
      disabledTooltip: isFromAccount
        ? generateAccountDisabledTooltip(t)
        : generateDisabledTooltip(t, derivedResourceType, "common:deleted"),
      tooltipPlacement: "left",
    },
  ].filter(menuItem => {
    return (
      _.includes(menuItem.resourceTypes, derivedResourceType) &&
      menuItem.visible
    );
  });
};

const getOrganizationResourcesFilters = ({
  searchText,
  orderBy,
  orderByDirection,
  visibleTo,
  first,
  organizationId,
  curriculumProgramId,
  orgFilterGradeIds,
  searchMode,
  states,
  schoolIds,
  isCombinedView,
  createdBy,
  lastUpdatedBy,
}) => {
  const isCCHEnabled = isSchoolPoliciesForCCHEnabled();
  const useSchoolIds =
    isCombinedView || (isCCHEnabled && !_.isEmpty(schoolIds));
  const curriculumFilter = useSchoolIds
    ? { schoolIds }
    : { curriculumProgramId };

  const getSearchFilter = () => {
    if (!searchMode.mode) return {};
    if (isCombinedView) return {};
    return {
      searchParentResourceId: curriculumProgramId,
      searchParentResourceType: "CURRICULUM_PROGRAM",
    };
  };
  const searchFilter = getSearchFilter();

  const props = {
    parentId: organizationId,
    parentType: "ORGANIZATION",
    first,
    organizationResourceFilter: {
      ...curriculumFilter,
      types: ["POLICIES_AND_RESOURCES"],
      visibleTo,
      searchText,
      gradeIds: orgFilterGradeIds,
      ...searchFilter,
    },
  };
  if (orderBy) props["orderBy"] = orderBy;
  if (orderByDirection) props["orderByDirection"] = orderByDirection;
  if (states) props.organizationResourceFilter["states"] = states;
  if (!_.isEmpty(createdBy))
    props.organizationResourceFilter["createdBy"] = createdBy;
  if (!_.isEmpty(lastUpdatedBy))
    props.organizationResourceFilter["lastUpdatedBy"] = lastUpdatedBy;

  return props;
};

const getFolderResourcesFilters = ({
  visibleTo,
  types,
  searchText,
  first,
  currentParentId,
  orderBy,
  orderByDirection,
  orgFilterGradeIds,
  searchMode,
  states,
  createdBy,
  lastUpdatedBy,
}) => {
  const props = {
    filters: {
      visibleTo,
      types,
      searchText,
      gradeIds: orgFilterGradeIds,
      ...(searchMode.mode
        ? {
            searchParentResourceId: currentParentId,
            searchParentResourceType: "FOLDER",
          }
        : {}),
    },
    first,
    id: currentParentId,
  };
  if (orderBy) props["orderBy"] = orderBy;
  if (orderByDirection) props["orderByDirection"] = orderByDirection;
  if (states) props.filters["states"] = states;
  if (!_.isEmpty(createdBy)) props.filters["createdBy"] = createdBy;
  if (!_.isEmpty(lastUpdatedBy)) props.filters["lastUpdatedBy"] = lastUpdatedBy;

  return props;
};

const getUserCountByEntityType = ({
  selectedGradeIds,
  academicYearId,
  organizationId,
}) => {
  return {
    input: {
      entity: "GRADE",
      entityIds: selectedGradeIds,
      academicYearId,
    },
    organizationId,
  };
};

const getResourcesByType = ({ selectedItems, type }) => {
  return _.get(
    _.filter(selectedItems, item => item.resourceType == type),
    "length",
    0
  );
};

const getNonLinksCount = ({ selectedItems }) => {
  return _.size(
    _.filter(
      selectedItems,
      item =>
        item.resourceType == "FILE" && _.get(item, "attachment.type") !== "LINK"
    )
  );
};

const isResourceFromMapleBearCentralRepo = tags => {
  return mapleBearLikeOrg() && _.size(tags) > 0;
};

const getFolderAndFileTitle = ({ t, selectedItems }) => {
  const foldersCount = getResourcesByType({
    selectedItems,
    type: "FOLDER",
  });
  const filesCount = getResourcesByType({
    selectedItems,
    type: "FILE",
  });
  const folderCountString =
    foldersCount > 0
      ? t("common:lowercase", {
          text: t("common:folder_count", { count: foldersCount }),
        })
      : "";

  const fileCountString =
    filesCount > 0
      ? t("common:lowercase", {
          text: t("common:file_count", { count: filesCount }),
        })
      : "";

  const stringWithAnd = t("common:join_array_with_and", {
    array: _.filter([folderCountString, fileCountString], string => {
      if (string) {
        return string;
      }
    }),
  });
  return stringWithAnd;
};

const getRetchQueries = ({
  organizationResourcesFeedQueryVariables,
  queryParamsFolders,
  parentResourceId,
}) => {
  return [
    !parentResourceId
      ? {
          query: getOrganizationResourcesFeedQuery,
          variables: organizationResourcesFeedQueryVariables,
        }
      : {
          query: getOrganizationFolderResourcesQuery,
          variables: queryParamsFolders,
        },
  ];
};

const isEducatorCenterRevampParentPermEnabled = () => {
  return ACLStore.can(EDUCATOR_CENTER_REVAMP_PARENT_PERM);
};

const isSchoolPoliciesForCCHEnabled = () => {
  return ACLStore.can(SCHOOL_POLICIES_FOR_CCH);
};

const getShareUserCountMap = ({ isProCurriculum }) => {
  return {
    TEACHER: {
      title: "schoolSetup:staff_members",
      icon: <UserProfileOutlined />,
    },
    ...(!isProCurriculum
      ? {
          STUDENT: {
            title: "classRoom:students",
            icon: <StudentOutlined />,
          },
          PARENT: {
            title: "common:family_member_plural",
            icon: <FamilyOutlined />,
          },
        }
      : {}),
  };
};

const getShareWithFilterOptions = ({ t, isProCurriculum }) => {
  const shareWithFilterOptions = [
    {
      value: "ADMIN",
      label: t("common:admin_plural"),
      isVisible: true,
    },
    {
      value: "TEACHER",
      label: t("schoolSetup:staff_members"),
      isVisible: true,
    },
    {
      value: "STUDENT",
      label: t("classRoom:students"),
      isVisible: !isProCurriculum,
    },
    {
      value: "PARENT",
      label: t("common:family_member_plural"),
      isVisible: !isProCurriculum,
    },
  ];
  return _.filter(shareWithFilterOptions, "isVisible");
};

const userTypeToVisibleToMap = {
  staff: ["TEACHER"],
  student: ["STUDENT"],
  parent: ["PARENT"],
};

const userTypeToQueryVarMap = {
  student: "STUDENT",
  staff: "STAFF",
  parent: "FAMILY_MEMBER",
};

const getViewOptions = t => [
  {
    value: "list",
    icon: <ListDashedOutlined />,
    label: t("common:list_view"),
  },
  {
    value: "grid",
    icon: <GridOutlined />,
    label: t("common:grid_view"),
  },
];

const getResourceTypeLocaleKey = ({ resourceType = "", mimeType = "" }) => {
  if (resourceType === "FOLDER") {
    return "common:folder";
  }

  if (resourceType === "LINK") {
    return "common:link";
  }

  if (resourceType === "FILE" && mimeType) {
    const normalizeMimeType = _.trim(mimeType);
    const fileType = getFileType(normalizeMimeType);
    return fileType?.locale;
  }

  return "common:file";
};

const getResolvedGradeIds = ({
  isAdminPage,
  isAccountEntry,
  userGradeIds,
  gradeIds,
  accountGrades,
  allSchoolGrades,
  schoolIds,
  resolvedSchoolIds,
}) => {
  if (!isAdminPage) return userGradeIds;
  if (!isAccountEntry || _.isEmpty(gradeIds)) return gradeIds;

  const selectedAccountGrades = _.filter(accountGrades, g =>
    _.includes(gradeIds, g.id)
  );
  const selectedGlobalGradeIds = _.compact(
    _.map(selectedAccountGrades, "globalGrade.id")
  );

  if (_.isEmpty(selectedGlobalGradeIds)) return gradeIds;
  if (_.isEmpty(allSchoolGrades)) return gradeIds;

  const schoolGradesInScope = _.isEmpty(schoolIds)
    ? allSchoolGrades
    : _.filter(allSchoolGrades, g =>
        _.includes(resolvedSchoolIds, _.get(g, "curriculumProgram.id"))
      );

  return _.compact(
    _.map(
      _.filter(schoolGradesInScope, g =>
        _.includes(selectedGlobalGradeIds, _.get(g, "globalGrade.id"))
      ),
      "id"
    )
  );
};

const getUserSelectedSchoolPoliciesProgrammesPreference = () => {
  return _.get(
    getSettingValue({
      name: SCHOOL_POLICIES_SELECTED_PROGRAMS_FOR_CCH,
      readFromUserAndOrgLevel: true,
    }),
    "selectedProgrammes",
    []
  );
};

export {
  getActionItems,
  getShareUserCountMap,
  userTypeToVisibleToMap,
  userTypeToQueryVarMap,
  getOrganizationResourcesFilters,
  getFolderResourcesFilters,
  getUserCountByEntityType,
  getRetchQueries,
  getNonLinksCount,
  getResourcesByType,
  isResourceFromMapleBearCentralRepo,
  getFolderAndFileTitle,
  getShareWithFilterOptions,
  isEducatorCenterRevampParentPermEnabled,
  isSchoolPoliciesForCCHEnabled,
  getViewOptions,
  getResourceTypeLocaleKey,
  getResolvedGradeIds,
  getUserSelectedSchoolPoliciesProgrammesPreference,
};
